<!--
 * @Author       : Bruce
 * @Date         : 2024-12-24 11:26:54
 * @LastEditors  : pan
 * @LastEditTime : 2025-07-11 10:00:00
 * @FilePath     : app/javascript/dashboard/routes/dashboard/conversation/contact/ContactInfo.vue
 * @Description  : 网站聊天对话最右侧联系人面板中的联系人信息部分
 * @Description  : 联系人黑名单删除 404 问题修复
 * Copyright 2025 Bruce, All Rights Reserved.
 * 2024-12-24 11:26:54
-->

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import NewConversation from './NewConversation.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';
import { IFrameHelper } from 'widget/helpers/utils';

export default {
  components: {
    ContactInfoRow,
    EditContact,
    Thumbnail,
    SocialIcons,
    NewConversation,
    ContactMergeModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeIconName: {
      type: String,
      default: 'chevron-right',
    },
  },
  emits: ['togglePanel', 'panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showConversationModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      showBlockModal: false,
      showUnblockModal: false,
      unblockModalTitle: '', // 解除黑名单模态框标题
      unblockModalMessage: '', // 解除黑名单模态框消息
      isBlockLoading: false,
      isUnBlockLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      isContactBlacklisted: 'contacts/isContactBlacklisted',
      getContactBlacklistType: 'contacts/getContactBlacklistType',
      getContactBlacklistStatus: 'contacts/getContactBlacklistStatus',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    isBlacklisted() {
      return this.isContactBlacklisted(this.contact.id);
    },
    blacklistType() {
      return this.getContactBlacklistType(this.contact.id);
    },
    // 判断是否只有IP黑名单（按钮需要置灰）
    hasOnlyIpBlacklist() {
      return this.blacklistType === 'ip';
    },
    // 判断是否有联系人黑名单（需要显示解除按钮）
    hasContactBlacklist() {
      return this.blacklistType === 'contact' || this.blacklistType === 'both';
    },
    // 判断黑名单按钮是否应该禁用
    isBlacklistButtonDisabled() {
      return this.uiFlags.isCheckingBlacklist || this.hasOnlyIpBlacklist;
    },
  },
  mounted() {
    // 监听定时检查黑名单事件
    emitter.on('check.blacklist.schedule', this.handleScheduledCheck);
  },
  beforeUnmount() {
    // 移除定时检查事件监听器
    emitter.off('check.blacklist.schedule', this.handleScheduledCheck);
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    onPanelToggle() {
      this.$emit('togglePanel');
    },
    toggleConversationModal() {
      this.showConversationModal = !this.showConversationModal;
      emitter.emit(
        BUS_EVENTS.NEW_CONVERSATION_MODAL,
        this.showConversationModal
      );
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    openBlockModal() {
      this.showBlockModal = true;
    },
    closeBlockModal() {
      this.showBlockModal = false;
    },
    // 确认添加联系人黑名单
    async confirmBlock() {
      this.isBlockLoading = true;
      try {
        // 构建联系人黑名单数据
        const blacklistData = {
          contacts_id: this.contact.id,
          blocked_email: this.contact.email,
          name: this.contact.name,
          blocked_phone: this.contact.phone_number,
          created_at: this.contact.last_activity_at,
        };

        // 调用API添加联系人黑名单
        await this.$store.dispatch('blacklist/create', blacklistData);

        // 更新黑名单状态
        await this.$store.dispatch(
          'contacts/checkBlacklistStatus',
          this.contact.id
        );
        this.closeBlockModal();
        useAlert(this.$t('BLACKLIST.ALERTS.ADD_SUCCESS'));
      } catch (error) {
        // 处理409冲突错误（联系人已在黑名单中）
        if (error.response && error.response.status === 409) {
          // 联系人已在黑名单中，刷新状态并给出成功提示
          await this.$store.dispatch(
            'contacts/checkBlacklistStatus',
            this.contact.id
          );
          this.closeBlockModal();
          useAlert(this.$t('BLACKLIST.ALERTS.ADD_SUCCESS'));
        } else {
          // 其他错误
          useAlert(
            error.response?.data?.error ||
              error.message ||
              this.$t('BLACKLIST.ALERTS.ADD_FAILURE')
          );
        }
      } finally {
        this.isBlockLoading = false;
      }
    },
    // 打开解除联系人黑名单模态框
    openUnblockModal() {
      try {
        // 获取黑名单状态数据
        const blacklistStatus = this.$store.getters[
          'contacts/getContactBlacklistStatus'
        ](this.contact.id);
        const { contactBlacklist } = blacklistStatus;

        // 检查是否有联系人黑名单
        if (!contactBlacklist) {
          throw new Error(this.$t('BLACKLIST.LIST.EMPTY_RESULT.404'));
        }

        // 设置确认对话框的标题和内容
        this.unblockModalTitle = this.$t('BLACKLIST.MODALS.DELETE.TITLE');
        this.unblockModalMessage = this.$t('BLACKLIST.MODALS.DELETE.MESSAGE');

        // 显示确认对话框
        this.showUnblockModal = true;
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('BLACKLIST.LIST.EMPTY_RESULT.404')
        );
      }
    },
    closeUnblockModal() {
      this.showUnblockModal = false;
    },
    // 确认解除联系人黑名单
    async confirmUnblock() {
      this.isUnBlockLoading = true;
      try {
        // 获取黑名单状态数据
        const blacklistStatus = this.$store.getters[
          'contacts/getContactBlacklistStatus'
        ](this.contact.id);
        const { contactBlacklist } = blacklistStatus;

        // 解除联系人黑名单
        if (contactBlacklist && contactBlacklist.id) {
          await this.$store.dispatch('blacklist/delete', contactBlacklist.id);
        }

        // 更新黑名单状态
        await this.$store.dispatch(
          'contacts/checkBlacklistStatus',
          this.contact.id
        );

        // 显示成功提示
        useAlert(this.$t('BLACKLIST.ALERTS.DELETE_SUCCESS'));
      } catch (error) {
        if (error.response && error.response.status === 404) {
          useAlert(this.$t('BLACKLIST.ALERTS.DELETE_SUCCESS'));
          await this.$store.dispatch(
            'contacts/checkBlacklistStatus',
            this.contact.id
          );
        } else {
          useAlert(
            error.message
              ? error.message
              : this.$t('BLACKLIST.ALERTS.DELETE_FAILURE')
          );
        }
      } finally {
        this.isUnBlockLoading = false;
        // 关闭确认对话框
        this.closeUnblockModal();
      }
    },

    // 处理定时检查黑名单事件
    handleScheduledCheck({ contactId }) {
      // 只处理当前联系人
      if (this.contact && this.contact.id === parseInt(contactId, 10)) {
        this.$store.dispatch('contacts/checkBlacklistStatus', contactId);
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Thumbnail
          v-if="showAvatar"
          :src="contact.thumbnail"
          size="56px"
          :username="contact.name"
          :status="contact.availability_status"
        />
        <woot-button
          v-if="showCloseButton"
          :icon="closeIconName"
          class="clear secondary rtl:rotate-180"
          @click="onPanelToggle"
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-start w-full min-w-0 gap-2">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-slate-800 dark:text-slate-100"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-1">
            <fluent-icon
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              icon="info"
              size="14"
              class="mt-0.5"
            />
            <a
              :href="contactProfileLink"
              class="text-base"
              target="_blank"
              rel="noopener nofollow noreferrer"
            >
              <woot-button
                size="tiny"
                icon="open"
                variant="clear"
                color-scheme="secondary"
              />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
          />
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.NEW_MESSAGE')"
          :title="$t('CONTACT_PANEL.NEW_MESSAGE')"
          icon="chat"
          size="small"
          @click="toggleConversationModal"
        />
        <woot-button
          v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
          :title="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="edit"
          variant="smooth"
          size="small"
          @click="toggleEditModal"
        />
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
          :title="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="merge"
          variant="smooth"
          size="small"
          color-scheme="secondary"
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <woot-button
          v-if="isAdmin"
          v-tooltip="$t('DELETE_CONTACT.BUTTON_LABEL')"
          :title="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="delete"
          variant="smooth"
          size="small"
          color-scheme="alert"
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
        <!-- 有联系人黑名单或同时有IP和联系人黑名单时显示解除黑名单按钮 -->
        <template v-if="hasContactBlacklist">
          <woot-button
            v-tooltip="$t('BLACKLIST.UNBLOCK_CONTACT')"
            :title="$t('BLACKLIST.UNBLOCK_CONTACT')"
            variant="smooth"
            size="small"
            color-scheme="success"
            :disabled="uiFlags.isCheckingBlacklist"
            @click="openUnblockModal"
          >
            {{ $t('BLACKLIST.UNBLOCK_CONTACT') }}
          </woot-button>
        </template>
        <!-- 没有黑名单或只有IP黑名单时显示添加黑名单按钮 -->
        <template v-else>
          <woot-button
            v-tooltip="
              hasOnlyIpBlacklist
                ? $t('BLACKLIST.IP_BLACKLIST.TITLE')
                : $t('BLACKLIST.BLOCK_CONTACT')
            "
            :title="$t('BLACKLIST.BLOCK_CONTACT')"
            icon="warning"
            variant="smooth"
            size="small"
            color-scheme="alert"
            :disabled="isBlacklistButtonDisabled"
            @click="openBlockModal"
          />
        </template>
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <NewConversation
        v-if="contact.id"
        :show="showConversationModal"
        :contact="contact"
        @cancel="toggleConversationModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
      <!-- 添加联系人黑名单确认弹框 -->
      <woot-delete-modal
        v-if="showBlockModal"
        v-model:show="showBlockModal"
        :on-close="closeBlockModal"
        :on-confirm="confirmBlock"
        :title="$t('BLACKLIST.MODALS.ADD.TITLE')"
        :message="$t('BLACKLIST.MODALS.ADD.MESSAGE')"
        :confirm-text="$t('BLACKLIST.MODALS.ADD.CONFIRM')"
        :reject-text="$t('BLACKLIST.MODALS.ADD.CANCEL')"
        :has-confirm-loading="isBlockLoading"
      />
      <!-- 解除联系人黑名单确认弹框 -->
      <woot-delete-modal
        v-if="showUnblockModal"
        v-model:show="showUnblockModal"
        :on-close="closeUnblockModal"
        :on-confirm="confirmUnblock"
        :title="unblockModalTitle"
        :message="unblockModalMessage"
        :confirm-text="$t('BLACKLIST.CONFIRM')"
        :reject-text="$t('BLACKLIST.CANCEL')"
        :has-confirm-loading="isUnBlockLoading"
      />
      <woot-delete-modal
        v-if="showDeleteModal"
        v-model:show="showDeleteModal"
        :on-close="closeDelete"
        :on-confirm="confirmDeletion"
        :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
        :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
        :message-value="confirmDeleteMessage"
        :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
        :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
      />
    </div>
  </div>
</template>
